import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
} from "recharts";
import { TrendingUp, Users, Building2, ShieldCheck, ExternalLink } from "lucide-react";

export default function PublicInsightsSection() {
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/feedback/public/insights").then(({ data }) => setD(data)).catch(() => {});
  }, []);

  if (!d || (d.ours.count === 0 && d.external.count === 0)) return null;

  const compareData = [
    { name: "TridentDigitalPulse", avg: d.ours.avg, count: d.ours.count, fill: "#FF3B30" },
    { name: "External vendors", avg: d.external.avg, count: d.external.count, fill: "#0A0A0A" },
  ];

  const ourPositivePct = d.ours.count ? Math.round((d.ours.positive / d.ours.count) * 100) : 0;
  const extPositivePct = d.external.count ? Math.round((d.external.positive / d.external.count) * 100) : 0;

  return (
    <section className="rounded-3xl border border-foreground/10 bg-white p-6 sm:p-10 lg:p-14" data-testid="public-insights-section">
      <div className="grid gap-8 lg:grid-cols-5">
        <div className="lg:col-span-2">
          <div className="inline-flex items-center gap-2 rounded-full bg-brand-ink px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white">
            <TrendingUp size={12} /> Voice of the market
          </div>
          <h2 className="mt-3 font-heading text-3xl font-extrabold leading-[1.05] tracking-tight sm:text-4xl">
            The data speaks<br />
            <span className="text-brand-orange">for itself.</span>
          </h2>
          <p className="mt-4 max-w-md text-sm leading-relaxed text-muted-foreground">
            Live, anonymised feedback data from <b className="text-foreground">{d.total.toLocaleString()}</b> customers who have rated
            us and vendors they've used elsewhere. No cherry-picking, no PR spin.
          </p>
          <div className="mt-6 space-y-3">
            <MetricRow label="Positive share (us)" value={`${ourPositivePct}%`} color="#16a34a" />
            <MetricRow label="Positive share (external vendors)" value={`${extPositivePct}%`} color="#737373" />
          </div>
          <div className="mt-4 flex items-center gap-2 text-[11px] text-muted-foreground">
            <ShieldCheck size={12} className="text-brand-orange" />
            All names anonymised · updated every hour · sourced from the public feedback form
          </div>
        </div>

        <div className="lg:col-span-3 space-y-4">
          <div className="rounded-2xl border p-5">
            <div className="mb-3 flex items-center gap-2">
              <Building2 size={14} className="text-brand-orange" />
              <div className="font-heading text-sm font-bold uppercase tracking-widest">
                Average rating · us vs external vendors
              </div>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={compareData} margin={{ top: 10, right: 20, bottom: 0, left: -10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f1f1" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis domain={[0, 5]} tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}★`} />
                <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} formatter={(v) => [`${v.toFixed(2)}★`, "Avg"]} />
                <Bar dataKey="avg" radius={[6, 6, 0, 0]}>
                  {compareData.map((r, i) => <Cell key={i} fill={r.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="mt-2 flex justify-around text-[11px] text-muted-foreground">
              <span>{d.ours.count} reviews about us</span>
              <span>{d.external.count} about others</span>
            </div>
          </div>

          {d.vendors.length > 0 && (
            <div className="rounded-2xl border p-5">
              <div className="mb-3 flex items-center gap-2">
                <Users size={14} className="text-brand-orange" />
                <div className="font-heading text-sm font-bold uppercase tracking-widest">
                  Vendors customers are talking about
                </div>
              </div>
              <ol className="space-y-2" data-testid="public-top-vendors">
                {d.vendors.map((v, i) => {
                  const slug = v.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
                  return (
                    <li key={v.name}>
                      <Link
                        to={`/insights/${slug}`}
                        data-testid={`vendor-link-${slug}`}
                        className="group flex items-center justify-between rounded-xl border p-3 transition hover:border-brand-orange/40 hover:bg-brand-orange/5"
                      >
                        <div className="flex min-w-0 items-center gap-2">
                          <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-brand-ink text-[10px] font-bold text-white">{i + 1}</span>
                          <div className="min-w-0">
                            <div className="truncate text-sm font-semibold group-hover:text-brand-orange">
                              {v.name}
                            </div>
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{v.count} mentions · view page</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-bold">
                            {v.avg.toFixed(1)}<span className="ml-1 text-brand-yellow">★</span>
                          </span>
                          <ExternalLink size={12} className="text-muted-foreground group-hover:text-brand-orange" />
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ol>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function MetricRow({ label, value, color }) {
  return (
    <div className="flex items-center justify-between rounded-xl border p-3">
      <span className="text-sm font-semibold">{label}</span>
      <span className="font-heading text-2xl font-extrabold" style={{ color }}>{value}</span>
    </div>
  );
}
